import type { Metadata } from "next";
import { env, isProductionSite } from "@/lib/environment";

export const site = {
  name: "Pickcel - Digital Signage Software Solution",
  description:
    "Run Stunning Digital Signage Displays with Pickcel's On-Premise & Cloud-Based Digital Signage Software Solutions. Quick Setup & User-Friendly Interface.",
  url: env.NEXT_PUBLIC_HOSTED_SITE_URL.replace(/\/$/, ""),
  ogImage: env.NEXT_PUBLIC_CDN_URL + "/images/shared/og/og-default.jpeg",
};

export function baseMetadata(overrides?: Metadata): Metadata {
  return {
    metadataBase: new URL(site.url),
    title: { default: site.name, template: `%s | ${site.name}` },
    description: site.description,
    alternates: { canonical: "/" },
    keywords: [
      "digital signage software",
      "digital signage solution",
      "cloud digital signage",
      "digital signage company",
      "digital signage CMS",
      "digital menu board software",
      "video wall software",
      "digital signage player",
    ],
    authors: [{ name: "Pickcel" }],
    creator: "Pickcel",
    publisher: "Pickcel",
    icons: {
      icon: [
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/favicon-16x16.png",
          sizes: "16x16",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/favicon-32x32.png",
          sizes: "32x32",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/favicon-96x96.png",
          sizes: "96x96",
          type: "image/png",
        },
      ],
      apple: [
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-57x57.png",
          sizes: "57x57",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-60x60.png",
          sizes: "60x60",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-72x72.png",
          sizes: "72x72",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-76x76.png",
          sizes: "76x76",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-114x114.png",
          sizes: "114x114",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-120x120.png",
          sizes: "120x120",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-144x144.png",
          sizes: "144x144",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-152x152.png",
          sizes: "152x152",
          type: "image/png",
        },
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/apple-icon-180x180.png",
          sizes: "180x180",
          type: "image/png",
        },
      ],
      other: [
        {
          url: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/android-icon-192x192.png",
          sizes: "192x192",
          type: "image/png",
        },
      ],
    },
    // manifest: env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/manifest.json",
    other: {
      "msapplication-TileColor": "#ffffff",
      "msapplication-TileImage":
        env.NEXT_PUBLIC_CDN_URL + "/images/shared/favicon/ms-icon-144x144.png",
      "theme-color": "#fff",
    },
    openGraph: {
      type: "website",
      url: site.url,
      siteName: "PickcelDigitalSignage",
      title: site.name,
      description: site.description,
      images: [site.ogImage],
      locale: "en_US",
    },
    twitter: {
      card: "summary_large_image",
      site: "@PickcelSignage",
      title: site.name,
      description: site.description,
      images: [site.ogImage],
    },
    // Only the production host is indexable. On internal/staging/preview hosts
    // we emit noindex,nofollow (crawling stays allowed via robots.txt so Google
    // actually reads this directive and drops the URLs).
    robots: isProductionSite
      ? {
          index: true,
          follow: true,
          googleBot: {
            index: true,
            follow: true,
            "max-image-preview": "large",
            "max-snippet": -1,
          },
        }
      : {
          index: false,
          follow: false,
          nocache: true,
          googleBot: {
            index: false,
            follow: false,
          },
        },
    // verification: {
    // google: "your-google-verification-code",
    // yandex: "your-yandex-verification-code",
    // yahoo: "your-yahoo-verification-code",
    // },
    ...overrides,
  };
}

export function jsonLd<T extends object>(obj: T) {
  return { __html: JSON.stringify(obj) };
}

// Schema generation helpers - moved to avoid circular dependencies
// Use the schema components directly instead

// Common schema options for different page types
export const commonSchemaOptions = {
  homepage: {
    pageName: site.name,
    pageUrl: site.url,
    keywords: [
      "digital signage software",
      "digital signage solution",
      "cloud digital signage",
      "digital signage company",
      "digital signage CMS",
      "digital menu board software",
      "video wall software",
      "digital signage player",
    ] as string[],
    faqs: [
      {
        question: "What is a digital signage solution?",
        answer:
          "A digital signage solution is a comprehensive system that allows businesses to display dynamic content on digital screens, including images, videos, web content, and interactive elements, to engage customers and communicate information effectively.",
      },
      {
        question: "How does cloud digital signage work?",
        answer:
          "Cloud digital signage works by storing content and management tools on remote servers, allowing users to create, schedule, and update content from anywhere with an internet connection, making it more flexible and cost-effective than traditional on-premise solutions.",
      },
      {
        question: "What are the benefits of digital menu boards?",
        answer:
          "Digital menu boards offer real-time updates, dynamic pricing, promotional content, reduced printing costs, improved visual appeal, and the ability to showcase multiple menu items simultaneously, enhancing customer experience and operational efficiency.",
      },
    ] as Array<{ question: string; answer: string }>,
  },
};
